//#region libraries
import React, { useMemo } from "react";
// tremor
import { Subtitle } from "@tremor/react";
// redux
import { useSelector } from "react-redux";
import { selectSelectedTaskToView } from "_redux/slices/timeline/selected-task";
import { selectTimelineArrivalsBookingList, selectTimelineDeparturesBookingList } from "_redux/slices/timeline/timeline-data";
// bootstrap
import { Card } from "react-bootstrap";
// styles
import { Spacing } from "_styles";
import { DARK_GREEN, GRAY_DARK, GRAY_LIGHT, PRM_LIGHT_BLUE, TEXT_COLOR, } from "_styles/colors";
// helpers
import { getJavaScriptDate, getJobStatusColor, getOriginalBooking, } from "_utils/functions";
//#endregion

// ---------- Small presentational helpers ----------
const InfoItem = ({ label, children }) => (
    <div style={ITEM_STYLE}>
        <div style={LABEL_STYLE}>{label}</div>
        <div style={VALUE_STYLE}>{children}</div>
    </div>
);

const TimeItem = ({ label, date }) => {
    const time =
        date != null
            ? getJavaScriptDate(date).toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
            })
            : null;

    return <InfoItem label={label}>{time ?? "N/A"}</InfoItem>;
};

//basic component to render
const PaxJourney = ({ bookingId = "", type = "A" }) => {
    // redux state
    const arrivalBookings = useSelector(selectTimelineArrivalsBookingList);         // holds list of arrivals bookings returned from post request, used to get original document of task
    const departuresBookings = useSelector(selectTimelineDeparturesBookingList);    // holds list of departures bookings returned from post request, used to get original document of task
    const task = useSelector(selectSelectedTaskToView);                              //hold selected/clicked task/booking to view

    const isTask = Boolean(task?.assignedTime);

    // normalize booking id (strip "_IATA..." suffix if present)
    const originalBookingId = useMemo(() => {
        if (!bookingId) return "";
        return bookingId.includes("_IATA")
            ? bookingId.split("_IATA")[0]
            : bookingId;
    }, [bookingId]);

    const bookings = type === "A" ? arrivalBookings : departuresBookings;

    const original = useMemo(
        () => getOriginalBooking(bookings, originalBookingId) ?? {},
        [bookings, originalBookingId]
    );

    const journeyArray = useMemo(
        () =>
            (type === "A" ? original?.arrivalJourney : original?.departureJourney) ??
            [],
        [type, original]
    );

    const headerBgColor = useMemo(
        () => (isTask ? getJobStatusColor(task) : type === "A" ? DARK_GREEN : PRM_LIGHT_BLUE),
        [isTask, task, type]
    );

    const headerStyle = useMemo(
        () => ({ ...BASE_HEADER_STYLE, backgroundColor: headerBgColor }),
        [headerBgColor]
    );

    // Empty state
    if (!journeyArray.length) {
        return (
            <>
                <Subtitle>Pax Journey</Subtitle>
                <hr />
                <div style={EMPTY_STATE_STYLE}>
                    <Subtitle style={{ color: GRAY_DARK, margin: 0 }}>
                        No actions added yet !
                    </Subtitle>
                </div>
            </>
        );
    }

    return (
        <>
            <Subtitle>Pax Journey</Subtitle>
            <hr />
            <div style={CONTAINER_STYLE}>
                {journeyArray.map((journey, idx) => {
                    const key = journey?.id || journey?._id || idx;
                    const title =
                        (journey?.journey ? String(journey.journey).toUpperCase() : "—");

                    return (
                        <Card style={CARD_STYLE} key={key}>
                            <Card.Header style={headerStyle}>
                                <p style={{ margin: 0, color: "white", fontSize: 16 }}>
                                    {title}
                                </p>

                                {/* timestamp/agent */}
                                <div style={{ textAlign: "right" }}>
                                    {journey?.employeeName && (
                                        <p
                                            style={{
                                                margin: 0,
                                                color: "white",
                                                fontSize: 16,
                                                fontWeight: 600,
                                            }}
                                        >
                                            Agent: {journey.employeeName}
                                        </p>
                                    )}
                                </div>
                            </Card.Header>

                            <Card.Body style={CONTENT_STYLE}>
                                <div style={ROW_STYLE}>
                                    <InfoItem label="Pick Up">
                                        {journey?.pickUpLocation?.name ?? "N/A"}
                                    </InfoItem>
                                    <InfoItem label="Drop Off">
                                        {journey?.dropOffLocation?.name ?? "N/A"}
                                    </InfoItem>

                                    <TimeItem label="Confirm Time" date={journey?.acknowledgedTime} />
                                    <TimeItem label="At Loc Time" date={journey?.atLocationTime} />
                                    <TimeItem label="Start Time" date={journey?.startTime} />
                                    <TimeItem label="Complete Time" date={journey?.completeTime} />
                                </div>
                            </Card.Body>
                        </Card>
                    );
                })}
            </div>
        </>
    );
};

export default PaxJourney;

//#region Styles (module-level to avoid re-creating objects) ----------
const CARD_STYLE = {
    marginBottom: Spacing.SCALE_16,
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    backgroundColor: "white",
};

const BASE_HEADER_STYLE = {
    color: "white",
    padding: Spacing.SCALE_12,
    borderRadius: "8px 8px 0 0",
    margin: 0,
    display: "flex",
    justifyContent: "space-between",
};

const EMPTY_STATE_STYLE = {
    textAlign: "center",
    padding: Spacing.SCALE_32,
    color: GRAY_DARK,
    backgroundColor: GRAY_LIGHT,
    borderRadius: "8px",
    border: `1px solid ${GRAY_LIGHT}`,
};

const CONTAINER_STYLE = { marginBottom: Spacing.SCALE_24 };

const ROW_STYLE = {
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "flex-start",
    gap: Spacing.SCALE_16,
};

const ITEM_STYLE = {
    flex: 1,
    textAlign: "flex-start",
    backgroundColor: "transparent",
    border: "2px solid transparent",
    borderRadius: "6px",
    padding: 0,
    transition: "all 0.2s ease",
};

const LABEL_STYLE = {
    fontSize: 12,
    fontWeight: 600,
    color: GRAY_DARK,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.5,
};

const VALUE_STYLE = {
    fontSize: 14,
    color: TEXT_COLOR,
    margin: 0,
    fontWeight: 500,
};

const CONTENT_STYLE = { padding: Spacing.SCALE_16 };
//#endregion